import React, { useState, useEffect } from 'react';
import { Link, useNavigate, Routes, Route, NavLink } from 'react-router-dom';
import { FiUser, FiPackage, FiLogOut, FiEdit } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import { formatPrice, getOrderStatusColor } from '../utils/helpers';
import API from '../api/axios';
import toast from 'react-hot-toast';

const ProfileSection = () => {
  const { user, updateUser } = useAuth();
  const [form, setForm] = useState({ name: user?.name || '', phone: user?.phone || '', password: '', confirmPassword: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password && form.password !== form.confirmPassword) return toast.error('Passwords do not match');
    setLoading(true);
    try {
      const payload = { name: form.name, phone: form.phone };
      if (form.password) payload.password = form.password;
      const { data } = await API.put('/auth/profile', payload);
      updateUser(data);
      toast.success('Profile updated!');
      setForm((f) => ({ ...f, password: '', confirmPassword: '' }));
    } catch {
      toast.error('Error updating profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card p-6">
      <h2 className="font-bold text-xl mb-6">My Profile</h2>
      <form onSubmit={handleSubmit} className="space-y-4 max-w-md">
        <div>
          <label className="text-sm font-medium text-gray-700 mb-1 block">Full Name</label>
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-field" />
        </div>
        <div>
          <label className="text-sm font-medium text-gray-700 mb-1 block">Email</label>
          <input value={user?.email} disabled className="input-field bg-gray-50 text-gray-500" />
        </div>
        <div>
          <label className="text-sm font-medium text-gray-700 mb-1 block">Phone</label>
          <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="input-field" placeholder="03001234567" />
        </div>
        <div className="border-t border-gray-100 pt-4">
          <p className="text-sm font-medium text-gray-700 mb-3">Change Password (optional)</p>
          <div className="space-y-3">
            <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="input-field" placeholder="New password" />
            <input type="password" value={form.confirmPassword} onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })} className="input-field" placeholder="Confirm new password" />
          </div>
        </div>
        <button type="submit" disabled={loading} className="btn-primary flex items-center gap-2">
          <FiEdit size={15} /> {loading ? 'Saving...' : 'Save Changes'}
        </button>
      </form>
    </div>
  );
};

const OrdersSection = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get('/orders/mine').then(({ data }) => setOrders(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-center py-10 text-gray-400">Loading orders...</div>;

  return (
    <div>
      <h2 className="font-bold text-xl mb-6">My Orders</h2>
      {orders.length === 0 ? (
        <div className="card p-10 text-center text-gray-400">
          <FiPackage size={48} className="mx-auto mb-3 opacity-30" />
          <p>No orders yet</p>
          <Link to="/shop" className="btn-primary mt-4 inline-block text-sm">Start Shopping</Link>
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map((order) => (
            <div key={order._id} className="card p-5">
              <div className="flex flex-wrap justify-between items-start gap-3 mb-3">
                <div>
                  <p className="font-bold text-primary-600">{order.orderNumber}</p>
                  <p className="text-xs text-gray-500">{new Date(order.createdAt).toLocaleDateString('en-PK', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                </div>
                <div className="text-right">
                  <span className={`badge ${getOrderStatusColor(order.orderStatus)} capitalize`}>{order.orderStatus}</span>
                  <p className="font-bold text-gray-800 mt-1">{formatPrice(order.total)}</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {order.items.slice(0, 3).map((item, i) => (
                  <div key={i} className="text-xs text-gray-600 bg-gray-50 rounded-lg px-2 py-1">
                    {item.name} x{item.quantity}
                  </div>
                ))}
                {order.items.length > 3 && <span className="text-xs text-gray-400">+{order.items.length - 3} more</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const AccountPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  if (!user) {
    navigate('/login', { state: { from: '/account' } });
    return null;
  }

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  const navLinkClass = ({ isActive }) =>
    `flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${isActive ? 'bg-primary-50 text-primary-700' : 'text-gray-600 hover:bg-gray-50'}`;

  return (
    <div className="container-custom py-8">
      <h1 className="section-title mb-8">My Account</h1>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {/* Sidebar */}
        <div className="md:col-span-1">
          <div className="card p-5">
            <div className="flex items-center gap-3 mb-5 pb-5 border-b border-gray-100">
              <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
                <span className="text-primary-600 font-bold text-lg">{user.name[0]}</span>
              </div>
              <div>
                <p className="font-semibold text-gray-800">{user.name}</p>
                <p className="text-xs text-gray-500">{user.email}</p>
              </div>
            </div>
            <nav className="space-y-1">
              <NavLink to="/account" end className={navLinkClass}><FiUser size={15} /> Profile</NavLink>
              <NavLink to="/account/orders" className={navLinkClass}><FiPackage size={15} /> My Orders</NavLink>
              <button onClick={handleLogout} className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-red-500 hover:bg-red-50 w-full">
                <FiLogOut size={15} /> Logout
              </button>
            </nav>
          </div>
        </div>

        {/* Content */}
        <div className="md:col-span-3">
          <Routes>
            <Route index element={<ProfileSection />} />
            <Route path="orders" element={<OrdersSection />} />
          </Routes>
        </div>
      </div>
    </div>
  );
};

export default AccountPage;
